import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Lock } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useRedeemCapturedOrgInvite } from "@/hooks/useRedeemCapturedOrgInvite";
import AuthModal from "@/components/auth/AuthModal";
import { SIGN_UP_PROMPTS, isSignUpUnlock, type SignUpUnlock } from "@/components/auth/signUpPromptCopy";
import CanonicalTags from "@/components/seo/CanonicalTags";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import { CTA_TITLE_LG, CheckList, CtaBand, SignUpCta, WaitlistCta } from "@/components/marketing/parts";
import { prefersReducedMotion, revealSequence, riseIn, shouldAnimateEntrance } from "@/components/marketing/motion";
import { pendingInviteRedirect } from "@/lib/orgInvitations";
import { readCapturedOrgInvite } from "@/lib/orgInviteBootstrap";

const SAMPLE = "Illustrative sample — not a live result.";

const REPORT = [
  { rank: 1, initial: "M", name: "Maverick Real Estate", meta: "Team · Roseville, CA", score: 94, top: true },
  { rank: 2, initial: "C", name: "Crestline Realty Group", meta: "Team · Folsom, CA", score: 88 },
  { rank: 3, initial: "B", name: "Brightpath Realty", meta: "Brokerage · Sacramento, CA", score: 76 },
  { rank: 4, initial: "W", name: "Wildhaven Realty", meta: "Brokerage · Elk Grove, CA", score: 71 },
];

const FIT_FACTORS = ["Compensation model", "Lead support", "Training", "Benefits", "Review ratings"];

const STEPS = [
  {
    title: "Build your private profile",
    body: "Tell us what matters to you — culture, compensation, market and leadership style. Your profile stays completely private.",
  },
  { title: "Explore, compare, and follow your matches", body: "Browse the feed and run side-by-side comparisons." },
  {
    title: "Reach out when you're ready",
    body: "When the fit is real, you decide when to reach out. No recruiter ever knows you were looking until you say so.",
  },
];

const Hero = () => {
  const heroRef = useRef<HTMLElement>(null);
  const productRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = heroRef.current;
    const product = productRef.current;
    if (!root || !product) return;
    if (shouldAnimateEntrance()) {
      revealSequence(Array.from(root.querySelectorAll("[data-reveal]")));
      riseIn(product, 420);
    }
    if (prefersReducedMotion()) return;
    // Product depth: the report tilts from 9° to flat as it scrolls into view.
    let raf = 0;
    const tick = () => {
      raf = 0;
      const rect = product.getBoundingClientRect();
      const vh = window.innerHeight || 900;
      const progress = Math.max(0, Math.min(1, (vh - rect.top) / (vh * 0.75)));
      product.style.transform = `rotateX(${((1 - progress) * 9).toFixed(2)}deg)`;
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(tick);
    };
    tick();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <section ref={heroRef} style={{ padding: "clamp(48px,6.5cqi,96px) 0 0", overflow: "hidden" }}>
      <div className="mk-container">
        <div data-reveal="" className="mk-label">
          Explore Real Estate Teams & Brokerages
        </div>
        <div
          style={{
            marginTop: 20,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-end",
            justifyContent: "space-between",
            gap: "28px clamp(40px,6cqi,96px)",
          }}
        >
          <h1
            data-reveal=""
            className="mk-h1"
            style={{ flex: "1 1 620px", maxWidth: 780 }}
          >
            The career market most agents <span className="mk-teal">never see.</span>
          </h1>
          <div
            data-reveal=""
            className="mk-stack"
            style={{ flex: "1 1 300px", maxWidth: 400, alignItems: "flex-start", gap: 24, paddingBottom: 6 }}
          >
            <p className="mk-lede">
              Get to know teams and brokerages before making a move. Compare what matters to your business and your
              life, then reach out when you're ready.
            </p>
            <SignUpCta size="lg">Explore Your Options</SignUpCta>
          </div>
        </div>
        <div style={{ marginTop: "clamp(44px,5.5cqi,72px)", perspective: 1800 }}>
          <div
            ref={productRef}
            role="figure"
            aria-label="Sample Fit Score Report"
            style={{
              background: "#fff",
              border: "1px solid var(--mk-border)",
              borderBottom: 0,
              borderRadius: "8px 8px 0 0",
              boxShadow: "0 48px 96px -56px rgba(28,24,19,0.32)",
              transformOrigin: "50% 100%",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "12px 24px",
                flexWrap: "wrap",
                padding: "20px clamp(20px,2.6cqi,32px)",
                borderBottom: "1px solid var(--mk-border)",
              }}
            >
              <div style={{ display: "flex", alignItems: "baseline", gap: "6px 16px", flexWrap: "wrap" }}>
                <span style={{ font: "600 var(--mk-text-h3-sm)/1.2 var(--mk-sans)" }}>Your Fit Score report</span>
                <span className="mk-sample">Sacramento, CA · 4 teams matched</span>
              </div>
              <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
                <span style={{ font: "500 var(--mk-text-caption)/1 var(--mk-sans)", color: "var(--mk-muted)" }}>{SAMPLE}</span>
                <span
                  style={{
                    display: "inline-flex",
                    gap: 8,
                    alignItems: "center",
                    font: "500 var(--mk-text-caption)/1 var(--mk-sans)",
                    color: "var(--mk-primary)",
                  }}
                >
                  Anonymous mode
                  <span
                    aria-hidden="true"
                    style={{ width: 7, height: 7, borderRadius: "50%", background: "var(--mk-primary)" }}
                  />
                </span>
              </div>
            </div>
            <ol
              style={{
                listStyle: "none",
                margin: 0,
                padding: 0,
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,150px),1fr))",
                gap: 1,
                background: "#EEEAE2",
              }}
            >
              {REPORT.map((org) => (
                <li
                  key={org.name}
                  style={{
                    background: org.top ? "var(--mk-tint)" : "#fff",
                    boxShadow: org.top ? "inset 0 2px 0 var(--mk-primary)" : undefined,
                    padding: "clamp(18px,2.4cqi,32px)",
                    display: "flex",
                    flexDirection: "column",
                    gap: 18,
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 8,
                      flexWrap: "wrap",
                      minHeight: 24,
                    }}
                  >
                    <span
                      className="mk-mono"
                      style={{ font: "500 var(--mk-text-meta)/1.3 var(--mk-mono)", color: org.top ? "var(--mk-primary)" : "var(--mk-muted)" }}
                    >
                      {org.rank}
                    </span>
                    {org.top && (
                      <span className="mk-top-match">
                        <span className="mk-dot" aria-hidden="true" />
                        Top match
                      </span>
                    )}
                  </div>
                  <span
                    aria-hidden="true"
                    style={{
                      width: 44,
                      height: 44,
                      borderRadius: 6,
                      background: org.top ? "var(--mk-primary)" : "var(--mk-tint)",
                      color: org.top ? "#fff" : "var(--mk-primary)",
                      display: "grid",
                      placeItems: "center",
                      /* v3.2 exception: avatar initial sized to its 44px tile, not type scale */
                      font: "600 18px var(--mk-sans)",
                    }}
                  >
                    {org.initial}
                  </span>
                  <div className="mk-stack" style={{ gap: 4 }}>
                    <span style={{ font: "600 var(--mk-text-h3-sm)/1.2 var(--mk-sans)" }}>{org.name}</span>
                    <span
                      style={{
                        font: "400 var(--mk-text-small)/1.4 var(--mk-sans)",
                        color: org.top ? "var(--mk-ink-2)" : "var(--mk-muted)",
                      }}
                    >
                      {org.meta}
                    </span>
                  </div>
                  <div className="mk-stack" style={{ marginTop: "auto", gap: 6 }}>
                    <span
                      style={{
                        font: "500 var(--mk-text-data)/0.9 var(--mk-mono)",
                        color: org.top ? "var(--mk-primary)" : "var(--mk-ink)",
                      }}
                    >
                      {org.score}
                    </span>
                    <span
                      style={{
                        font: "500 var(--mk-text-caption)/1.3 var(--mk-sans)",
                        color: org.top ? "var(--mk-ink-2)" : "var(--mk-muted)",
                      }}
                    >
                      Fit Score
                    </span>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
};

const Audiences = () => (
  <section className="mk-section mk-section--white">
    <div
      className="mk-container"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,420px),1fr))",
        gap: "64px clamp(48px,8cqi,120px)",
      }}
    >
      {[
        {
          label: "For Agents",
          title: "Explore your options, privately.",
          body: "Explore, compare, and find your fit — with zero pressure and total anonymity.",
          items: [
            "Side-by-side comparisons",
            "Personalized Fit Score",
            "Culture & leadership insights",
            "100% free, always anonymous",
          ],
          cta: <SignUpCta style={{ marginTop: "auto" }}>Explore Your Options</SignUpCta>,
        },
        {
          label: "For Teams & Brokerages",
          title: "Get discovered by the right agents.",
          body: "Help agents understand what it's like to work with you. Share your culture, training, leadership and support before the first conversation.",
          items: [
            "Tell your organization's story",
            "Show your culture and values",
            "Explain your training and support",
            "Free during beta",
          ],
          cta: (
            <WaitlistCta variant="outline" style={{ marginTop: "auto" }}>
              Get Listed
            </WaitlistCta>
          ),
        },
      ].map((card) => (
        <div
          key={card.label}
          className="mk-stack"
          style={{ alignItems: "flex-start", gap: 20, paddingTop: 32, borderTop: "2px solid var(--mk-ink)" }}
        >
          <div className="mk-label">{card.label}</div>
          <h2 className="mk-h2">
            {card.title}
          </h2>
          <p className="mk-body-lg">{card.body}</p>
          <CheckList items={card.items} style={{ margin: "8px 0 12px" }} />
          {card.cta}
        </div>
      ))}
    </div>
  </section>
);

const Feed = () => (
  <section className="mk-section mk-section--lg">
    <div
      className="mk-container"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,420px),1fr))",
        gap: "48px clamp(48px,7cqi,112px)",
        alignItems: "start",
      }}
    >
      <div className="mk-stack mk-sticky mk-sticky--420" style={{ gap: 20, maxWidth: 480 }}>
        <div className="mk-label">01 — The Feed</div>
        <h2 className="mk-h2 mk-h2--feature">Browse the market. Without anyone watching.</h2>
        <p className="mk-body-lg" style={{ lineHeight: 1.6 }}>
          Browse company news, posts and updates. Follow organizations that interest you and use the available filters
          to explore what matters to you.
        </p>
      </div>
      <div role="figure" aria-label="Illustrative market feed sample" className="mk-stack" style={{ gap: 12 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            gap: 12,
            padding: "0 4px 12px",
          }}
        >
          <div className="mk-stack" style={{ gap: 6 }}>
            <span style={{ font: "600 var(--mk-text-h3)/1.25 var(--mk-sans)" }}>Your market feed</span>
            <span className="mk-sample">Sacramento, CA · {SAMPLE}</span>
          </div>
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              font: "500 var(--mk-text-caption)/1 var(--mk-sans)",
              color: "var(--mk-primary)",
            }}
          >
            <Lock size={16} aria-hidden="true" />
            Private
          </span>
        </div>
        {[
          {
            org: "Maverick Real Estate",
            tag: "Growth",
            text: "Growing fast — 15 new agents joined this quarter. Now at 62 total.",
            meta: "3 hours ago · Roseville, CA",
            style: { boxShadow: "0 20px 40px -32px rgba(28,24,19,0.25)" },
          },
          {
            org: "Crestline Realty Group · Folsom",
            tag: "Leadership",
            text: "New team lead appointed. Regional expansion underway — two new markets by Q3.",
            meta: "Yesterday · Folsom, CA",
            style: { marginLeft: "clamp(0px,3cqi,36px)" },
          },
        ].map((item) => (
          <article
            key={item.org}
            className="mk-card mk-stack"
            style={{ padding: 24, gap: 10, ...item.style }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline" }}>
              <span style={{ font: "600 var(--mk-text-h3-sm)/1.2 var(--mk-sans)" }}>{item.org}</span>
              <span style={{ font: "500 var(--mk-text-caption)/1 var(--mk-sans)", color: "var(--mk-primary)" }}>{item.tag}</span>
            </div>
            <p style={{ font: "400 var(--mk-text-body)/1.6 var(--mk-sans)" }}>{item.text}</p>
            <span style={{ font: "400 var(--mk-text-meta)/1.3 var(--mk-mono)", color: "var(--mk-muted)" }}>{item.meta}</span>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const COMPARISON_ROWS: { label: string; cells: { value: string; empty?: boolean; score?: boolean }[] }[] = [
  {
    label: "Fit Score",
    cells: [
      { value: "94", score: true },
      { value: "88", score: true },
      { value: "76", score: true },
    ],
  },
  {
    label: "Compensation model",
    cells: [{ value: "Capping Model" }, { value: "Fixed Commission" }, { value: "Not shared", empty: true }],
  },
  {
    label: "Fee type",
    cells: [{ value: "Transaction fee" }, { value: "Not shared", empty: true }, { value: "Transaction fee" }],
  },
];

const Comparison = () => (
  <section className="mk-section mk-section--lg mk-section--white mk-section--rule">
    <div
      className="mk-container"
      style={{ display: "flex", flexWrap: "wrap", gap: "48px clamp(40px,5cqi,80px)", alignItems: "center" }}
    >
      <div className="mk-stack" style={{ gap: 20, flex: "1 1 320px", maxWidth: 420 }}>
        <div className="mk-label">02 — Comparison</div>
        <h2 className="mk-h2 mk-h2--feature">Compare teams and brokerages side by side.</h2>
        <p className="mk-body-lg" style={{ lineHeight: 1.6 }}>
          Review available organization details and compensation models alongside each other.
        </p>
      </div>
      <div
        role="figure"
        aria-label="Illustrative comparison sample"
        className="mk-stack"
        style={{ gap: 12, flex: "1.6 1 520px", minWidth: 0 }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: "6px 16px",
            flexWrap: "wrap",
            padding: "0 4px",
          }}
        >
          <span style={{ font: "600 var(--mk-text-h3-sm)/1.2 var(--mk-sans)" }}>Comparison</span>
          <span className="mk-sample">{SAMPLE}</span>
        </div>
        <div
          className="mk-card"
          tabIndex={0}
          role="region"
          aria-label="Comparison table, scrolls horizontally"
          style={{ overflowX: "auto", boxShadow: "0 20px 40px -32px rgba(28,24,19,0.25)" }}
        >
          <table style={{ width: "100%", minWidth: 460, borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th scope="col" style={{ ...thStyle, width: 132 }}>
                  <span className="mk-sr-only">Detail</span>
                </th>
                {["Maverick Real Estate", "Crestline Realty Group", "Brightpath Realty"].map((name) => (
                  <th key={name} scope="col" style={thStyle}>
                    {name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map((row, r) => {
                const last = r === COMPARISON_ROWS.length - 1;
                const border = last ? 0 : "1px solid var(--mk-hair)";
                return (
                  <tr key={row.label}>
                    <th
                      scope="row"
                      style={{
                        padding: "14px 16px",
                        textAlign: "left",
                        font: "500 var(--mk-text-small)/1.4 var(--mk-sans)",
                        color: "var(--mk-ink-2)",
                        borderBottom: border,
                      }}
                    >
                      {row.label}
                    </th>
                    {row.cells.map((cell, c) => (
                      <td
                        key={c}
                        style={{
                          padding: "14px 16px",
                          borderBottom: border,
                          whiteSpace: cell.score ? undefined : "nowrap",
                          font: cell.score
                            ? "500 18px/1 var(--mk-mono)"
                            : `${cell.empty ? 400 : 500} 15px/1.3 var(--mk-sans)`,
                          color: cell.score ? "var(--mk-primary)" : cell.empty ? "var(--mk-muted)" : undefined,
                        }}
                      >
                        {cell.value}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </section>
);

const thStyle: React.CSSProperties = {
  padding: "14px 16px",
  textAlign: "left",
  font: "600 var(--mk-text-label-sm)/1.3 var(--mk-sans)",
  borderBottom: "1px solid var(--mk-border)",
  verticalAlign: "bottom",
};

const FitScore = () => (
  <section className="mk-section mk-section--lg mk-section--tint">
    <div
      className="mk-container"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,420px),1fr))",
        gap: "56px clamp(48px,7cqi,112px)",
        alignItems: "center",
      }}
    >
      <div className="mk-stack" style={{ gap: 20, maxWidth: 480 }}>
        <div className="mk-label">03 — Fit Score</div>
        <h2 className="mk-h2 mk-h2--feature">See how your preferences line up.</h2>
        <p className="mk-body-lg" style={{ color: "var(--mk-ink)", lineHeight: 1.6 }}>
          Your Fit Score compares your preferences with available team and brokerage information and review ratings,
          helping you choose which options to explore.
        </p>
      </div>
      <div role="figure" aria-label="Illustrative Fit Score sample" className="mk-stack" style={{ gap: 32 }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: "6px 16px", flexWrap: "wrap" }}>
          <span style={{ font: "600 var(--mk-text-h3-sm)/1.2 var(--mk-sans)" }}>Fit Score</span>
          <span className="mk-sample" style={{ color: "var(--mk-ink)" }}>
            {SAMPLE}
          </span>
        </div>
        <div style={{ display: "flex", gap: 32, alignItems: "center", flexWrap: "wrap" }}>
          <div
            aria-hidden="true"
            style={{
              width: "clamp(168px,17cqi,220px)",
              aspectRatio: "1",
              borderRadius: "50%",
              background: "conic-gradient(var(--mk-primary) 0 94%, #fff 94% 100%)",
              display: "grid",
              placeItems: "center",
              flex: "none",
            }}
          >
            <div
              style={{
                width: "86%",
                aspectRatio: "1",
                borderRadius: "50%",
                background: "var(--mk-tint)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
              }}
            >
              <span style={{ font: "500 var(--mk-text-data-ring)/1 var(--mk-mono)", color: "var(--mk-primary)" }}>
                94
              </span>
              <span style={{ font: "500 var(--mk-text-small)/1 var(--mk-sans)" }}>Fit Score</span>
            </div>
          </div>
          <div className="mk-stack" style={{ gap: 6, minWidth: 0 }}>
            <span className="mk-sr-only">Fit Score 94 for</span>
            <span style={{ font: "600 var(--mk-text-h3)/1.25 var(--mk-sans)" }}>Maverick Real Estate</span>
            <span style={{ font: "400 var(--mk-text-small)/1.4 var(--mk-sans)" }}>Team · Roseville, CA</span>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                font: "500 var(--mk-text-small)/1 var(--mk-sans)",
                marginTop: 6,
              }}
            >
              <span className="mk-dot" style={{ width: 7, height: 7 }} aria-hidden="true" />
              Top match
            </span>
          </div>
        </div>
        <div className="mk-stack" style={{ gap: 12, paddingTop: 28, borderTop: "1px solid var(--mk-tint-rule)" }}>
          <span style={{ font: "500 var(--mk-text-small)/1.4 var(--mk-sans)" }}>Uses the following information, where available and comparable</span>
          <div className="mk-chip-row">
            {FIT_FACTORS.map((factor) => (
              <span key={factor} className="mk-chip">
                {factor}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const HowItWorks = () => (
  <section id="how-it-works" className="mk-section mk-section--lg" style={{ scrollMarginTop: 96 }}>
    <div
      className="mk-container"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,380px),1fr))",
        gap: "48px clamp(48px,7cqi,112px)",
        alignItems: "start",
      }}
    >
      <div className="mk-stack mk-sticky mk-sticky--380" style={{ gap: 20 }}>
        <div className="mk-label">How It Works</div>
        <h2 className="mk-h2 mk-h2--lead">
          Three steps to your next move.
        </h2>
      </div>
      <ol
        style={{
          listStyle: "none",
          margin: 0,
          padding: 0,
          display: "flex",
          flexDirection: "column",
          borderTop: "1px solid var(--mk-ink)",
        }}
      >
        {STEPS.map((step, i) => (
          <li
            key={step.title}
            style={{
              display: "grid",
              gridTemplateColumns: "72px minmax(0,1fr)",
              gap: "8px 24px",
              padding: "32px 0",
              borderBottom: "1px solid var(--mk-border)",
            }}
          >
            <span style={{ font: "600 var(--mk-text-step)/1 var(--mk-sans)", color: "var(--mk-primary)" }}>0{i + 1}</span>
            <div className="mk-stack" style={{ gap: 10 }}>
              <h3 style={{ font: "600 var(--mk-text-h3)/1.25 var(--mk-sans)" }}>{step.title}</h3>
              <p style={{ font: "400 var(--mk-text-body)/1.6 var(--mk-sans)", color: "var(--mk-ink-2)" }}>{step.body}</p>
            </div>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

const LandingPage = () => {
  const [authModalOpen, setAuthModalOpen] = useState(false);
  // ?signup=<unlock>: open agent sign-up saying what the account unlocks
  // (founder decision F6). An invitation, when present, takes precedence.
  const [signUpUnlock, setSignUpUnlock] = useState<SignUpUnlock | null>(null);
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { redeem } = useRedeemCapturedOrgInvite();

  // The member-invite email links here: send-org-invite emits
  // `${siteUrl}?invite_token=<token>`.
  //
  // Unauthenticated: auto-open AuthModal in signup mode. Two distinct carriers
  // open signup — ?invite_token= (member invite, accepted after auth) and the
  // org-admin invite token, which src/lib/orgInviteBootstrap.ts has already
  // lifted out of the URL (#org_invite= fragment, or a legacy query) into memory.
  // AuthModal validates it and redeems it from either the signup or the sign-in
  // branch.
  //
  // Authenticated: hand off to /invite, the dedicated acceptance surface, which
  // redeems the token and renders the success/failure state. Without this the
  // effect returned early and a signed-in recipient clicking their invite link
  // just landed on the marketing page with nothing happening.
  useEffect(() => {
    if (authLoading) return;
    const search = window.location.search;

    if (isAuthenticated) {
      // An already-signed-in org-admin invitee still has a captured token: AuthModal
      // only runs for signed-out visitors, so the redemption is triggered from here.
      // See useRedeemCapturedOrgInvite for the behaviour.
      if (readCapturedOrgInvite()) {
        void redeem();
        return;
      }

      const redirect = pendingInviteRedirect(search);
      if (redirect) navigate(redirect, { replace: true });
      return;
    }

    const params = new URLSearchParams(search);
    if (params.get("invite_token") || readCapturedOrgInvite()) {
      setAuthModalOpen(true);
      return;
    }
    const unlock = params.get("signup");
    if (isSignUpUnlock(unlock)) {
      setSignUpUnlock(unlock);
      setAuthModalOpen(true);
    }
  }, [authLoading, isAuthenticated, navigate, redeem]);

  return (
    <MarketingLayout>
      <CanonicalTags path="/" />
      <Hero />
      <Audiences />
      <Feed />
      <Comparison />
      <FitScore />
      <HowItWorks />
      <CtaBand
        title="Find the place you'd actually stay."
        body="Free forever for agents. No credit card."
        padding="clamp(88px,11cqi,152px) var(--mk-gutter)"
        titleStyle={CTA_TITLE_LG}
        cta={
          <SignUpCta variant="inverse" size="lg" style={{ padding: "0 26px" }}>
            Get Started Free
          </SignUpCta>
        }
      />

      {/* Invitation sign-up (an invite_token link or a captured org-admin
          invitation). Invitation surfaces keep the product's default look. */}
      <AuthModal
        isOpen={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        defaultMode="signup"
        signUpIntro={signUpUnlock ? SIGN_UP_PROMPTS[signUpUnlock] : undefined}
      />
    </MarketingLayout>
  );
};

export default LandingPage;
